import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { PRICING, SERVICE_INCLUDES, BRAND } from "@/lib/constants";

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="bg-[#F8F8F8] py-16 sm:py-28 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: Pricing model */}
          <div className="lg:col-span-5">
            <p className="overline mb-5">
              <span className="inline-block w-10 h-px bg-[#D4AF37] align-middle mr-4" />
              Prismodel
            </p>
            <h2
              className="font-serif text-[2.25rem] sm:text-5xl lg:text-[3.75rem] text-[#111111] font-light tracking-tight leading-[1.05]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Du betaler kun, når{" "}
              <span className="italic text-[#C9A227]">vi tjener dig penge</span>.
            </h2>

            <p className="mt-8 text-[#555555] font-light leading-relaxed max-w-md">
              Ingen opstartsgebyr og ingen skjulte omkostninger. Du betaler kun
              en provision af din lejeindtægt - så vores succes hænger direkte
              sammen med din.
            </p>

            <div className="mt-10 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10 border-t border-black/10 pt-8 sm:pt-10 max-w-md">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div
                  className="font-serif text-5xl sm:text-7xl text-[#111111] font-light leading-none"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  0 kr.
                </div>
                <div className="text-[10px] sm:text-[11px] tracking-[0.18em] sm:tracking-[0.22em] uppercase text-[#555555] mt-3 sm:mt-4 leading-tight">
                  Opstartsomkostning
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <div
                  className="font-serif text-2xl sm:text-3xl text-[#111111] font-light leading-tight italic"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  Du betaler kun
                  <br />når <span className="text-[#C9A227]">vi tjener dig</span>
                </div>
              </motion.div>
            </div>

            <a
              href={BRAND.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-book-btn"
              className="btn-gold mt-12 inline-flex"
              style={{ background: "#D4AF37", borderColor: "#D4AF37", color: "#111111" }}
            >
              <span>Book et uforpligtende møde</span>
              <ArrowRight size={16} className="relative z-10" />
            </a>
          </div>

          {/* Right: What's included */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
            className="lg:col-span-7 bg-[#111111] text-white p-8 sm:p-14 lg:p-16 relative overflow-hidden"
            data-testid="pricing-included"
          >
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />

            <p
              className="overline mb-6 relative"
              style={{ color: "#D4AF37" }}
            >
              <span className="inline-block w-10 h-px bg-[#D4AF37] align-middle mr-4" />
              Hvad indeholder vores service
            </p>

            <h3
              className="font-serif text-3xl sm:text-4xl text-white font-light leading-tight relative"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Alt inkluderet -{" "}
              <span className="italic text-[#D4AF37]">altid</span>.
            </h3>

            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 relative">
              {SERVICE_INCLUDES.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-white/90 font-light text-sm sm:text-base leading-relaxed"
                >
                  <Check
                    size={18}
                    className="text-[#D4AF37] mt-1 shrink-0"
                    strokeWidth={1.5}
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 pt-8 border-t border-white/10 flex items-center justify-between gap-6 relative">
              <span className="text-xs tracking-[0.25em] uppercase text-white/60">
                Klar månedlig rapport · Fuld gennemsigtighed
              </span>
              <span
                className="font-serif text-xl sm:text-2xl text-[#D4AF37] font-light italic whitespace-nowrap"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                Alt-inkl.
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
